/**
 * Account — basic profile + plan info surface. Opens from the Sidebar's
 * "Account info" entry. Uses the existing /api/auth/me for identity and
 * /api/billing/status for plan/trial. Read-only today; editable in a
 * future iteration.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  User, Shield, CreditCard, Mail, Calendar, CheckCircle2, LogOut, ArrowRight,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import TwoFactorPanel from "../components/TwoFactorPanel";
import NotificationPrefsPanel from "../components/NotificationPrefsPanel";
import LanguagePreferencePanel from "../components/LanguagePreferencePanel";
import { SettingsTabs } from "../components/SettingsTabs";
import { useLang } from "../i18n/LangContext";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const { user, logout } = useAuth();
  const [billing, setBilling] = useState(null);
  useEffect(() => {
    axios.get(`${API}/billing/status`).then(({ data }) => setBilling(data)).catch(() => setBilling({ plan: "Free", trial_active: false }));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="account-page">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <header className="sticky top-0 z-20 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-8 py-4 flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#0A1233] text-[#E5C572] flex items-center justify-center"><User className="w-5 h-5" /></div>
            <div className="leading-tight">
              <h1 className="text-[16px] font-bold">{isPt ? "Informações da conta" : "Account information"}</h1>
              <p className="text-[11px] text-[var(--al-text-muted)]">{isPt ? "Identidade, plano e preferências" : "Identity, plan and preferences"}</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2"><ThemeToggle /><LangToggle /></div>
        </header>
        <SettingsTabs isPt={isPt} />
        <main className="px-8 py-7 max-w-3xl mx-auto space-y-5">
          <section className="al-card p-6" data-testid="account-identity">
            <h2 className="text-[13px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-4">{isPt ? "Identidade" : "Identity"}</h2>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-[#C9A24E] text-[#0A1233] flex items-center justify-center text-[20px] font-black shrink-0">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2 text-[13px]">
                  <User className="w-3.5 h-3.5 text-[var(--al-text-muted)]" />
                  <span className="font-semibold">{user?.name || user?.email?.split("@")[0]}</span>
                </div>
                <div className="flex items-center gap-2 text-[13px]">
                  <Mail className="w-3.5 h-3.5 text-[var(--al-text-muted)]" />
                  <span>{user?.email || "—"}</span>
                </div>
                <div className="flex items-center gap-2 text-[12px]">
                  <Shield className="w-3.5 h-3.5 text-[var(--al-text-muted)]" />
                  <span className="uppercase tracking-wider font-bold text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: user?.role === "admin" ? "#FDE68A" : "#E0E7FF", color: user?.role === "admin" ? "#92400E" : "#1E3A8A" }}>
                    {user?.role || "user"}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="al-card p-6" data-testid="account-plan">
            <h2 className="text-[13px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-4">{isPt ? "Plano" : "Plan"}</h2>
            {billing ? (
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#10B98120] text-[#059669] flex items-center justify-center"><CreditCard className="w-5 h-5" /></div>
                <div className="flex-1">
                  <div className="font-display text-[20px] font-bold">{billing.plan || "Free"}</div>
                  {billing.trial_active && (
                    <div className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded mt-1" style={{ backgroundColor: "#D1FAE5", color: "#047857" }}>
                      <CheckCircle2 className="w-3 h-3" /> {isPt ? "Trial ativo" : "Trial active"}
                    </div>
                  )}
                  {billing.trial_days_remaining != null && (
                    <div className="text-[12px] text-[var(--al-text-muted)] mt-2 inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {billing.trial_days_remaining} {isPt ? "dias restantes" : "days remaining"}
                    </div>
                  )}
                </div>
                <a href="/billing"
                  data-testid="account-upgrade"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[12px] font-bold transition-colors">
                  UPGRADE <ArrowRight className="w-3 h-3" />
                </a>
              </div>
            ) : (
              <div className="text-[12.5px] text-[var(--al-text-muted)]">{isPt ? "Carregando…" : "Loading…"}</div>
            )}
          </section>

          <section className="al-card p-6" data-testid="account-actions">
            <h2 className="text-[13px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-4">{isPt ? "Ações" : "Actions"}</h2>
            <button type="button" onClick={logout}
              data-testid="account-logout"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-[var(--al-border)] hover:bg-rose-50 hover:border-rose-300 hover:text-rose-600 text-[13px] font-semibold transition-colors">
              <LogOut className="w-4 h-4" /> {isPt ? "Sair da conta" : "Sign out"}
            </button>
          </section>

          <TwoFactorPanel isPt={isPt} />
          <NotificationPrefsPanel isPt={isPt} />
          <LanguagePreferencePanel />
        </main>
      </div>
    </div>
  );
}
